<template>
  <ul
    v-if="showPagination"
    class="pagination-flat justify-content-center twbs-prev-next pagination pagination--on-foreground"
  >
    <li class="page-item prev" :class="{ 'disabled': intValue === 1 }">
      <a class="page-link" href="#" @click="goToPrevious" style="padding-right: 22px;">
        <arrow-left-icon/>
      </a>
    </li>

    <template v-if="intValue - sides > 1">
      <li class="page-item" key="first-page">
        <a class="page-link" href="#" @click="goToPage($event, 1)">
          1
        </a>
      </li>
      <li class="page-item" key="skipped-left-pages">
        <span class="page-link text-grey-theme">&hellip;</span>
      </li>
    </template>

    <template v-for="(n, index) in range" :key="index">
      <li class="page-item" :class="{ 'active': n === intValue }">
        <a class="page-link" href="#" @click="goToPage($event, n)">{{ n }}</a>
      </li>
    </template>

    <template v-if="intValue + props.sides < props.config.last_page">
      <li class="page-item" key="skipped-right-pages">
        <span class="page-link text-grey-theme">&hellip;</span>
      </li>
      <li class="page-item" key="last-page">
        <a class="page-link" href="#" @click="goToPage($event, props.config.last_page)">
          {{ props.config.last_page }}
        </a>
      </li>
    </template>


    <li class="page-item next" :class="{ 'disabled': intValue >= props.config.last_page }">
      <a class="page-link" href="#" @click="goToNext" style="padding-left: 22px;">
        <arrow-right-icon/>
      </a>
    </li>
  </ul>
</template>

<script setup>
  import ArrowLeftIcon from '@/components/icons/arrow-left';
  import ArrowRightIcon from '@/components/icons/arrow-right';
  import { computed } from 'vue';

  const emit = defineEmits(['change']);

  const props = defineProps({
    config: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number],
      required: true,
    },
    // How many pages are visible from the sides
    sides: {
      type: Number,
      default: 2,
    },
    edge: {
      type: Boolean,
      default: false,
    },
  });

  const showPagination = computed(() => Object.keys(props.config).length !== 0 && props.config?.last_page && props.config?.last_page !== 1);

  const intValue = computed(() => {
    if (isNaN(Number(props.value))) {
      return 0;
    }

    return Number(props.value);
  });

  const range = computed(() => {
    const maxVisibleSides = props.sides * 2;
    const maxVisible = maxVisibleSides + 1;

    // If total pages are less than maximum visible, show all
    if (props.config.last_page < maxVisible) {
      return getRange(1, props.config.last_page);
    }

    const pages = [];

    // Add pre-range of the pagination
    if (intValue.value + props.sides > props.config.last_page) {
      // Calculate how many items should be filled on the left to prevent whole page count shrink
      const fillableLeft = maxVisibleSides - (props.config.last_page - intValue.value);
      addRange(pages, Math.max(0, intValue.value - fillableLeft), intValue.value - 1);
    } else {
      addRange(pages, Math.max(1, intValue.value - props.sides), intValue.value - 1);
    }

    // Add current page
    pages.push(intValue.value);

    // Add post range
    if (intValue.value - props.sides <= 0) {
      // Calculate how many items should be filled on the right to prevent whole page count shrink
      const fillableRight = maxVisibleSides - intValue.value + 1;
      addRange(pages, intValue.value + 1, Math.min(intValue.value + fillableRight, props.config.last_page));
    } else {
      addRange(pages, intValue.value + 1, Math.min(intValue.value + props.sides, props.config.last_page));
    }

    return pages;
  });

  function goToPage(e, page) {
    e.preventDefault();

    if (page <= 0 || page > props.config.last_page || intValue.value === page) {
      return;
    }

    emit('change', page);
  }

  function goToPrevious(e) {
    e.preventDefault();

    if (intValue.value - 1 <= 0) {
      return;
    }

    goToPage(e, intValue.value - 1);
  }

  function goToNext(e) {
    e.preventDefault();

    if (intValue.value + 1 > props.config.last_page) {
      return;
    }

    goToPage(e, intValue.value + 1);
  }

  function addRange(arr, from, to) {
    for (let i = from; i <= to; i++) {
      arr.push(i);
    }
  }

  function getRange(from, to) {
    const entry = [];
    for (let i = from; i <= to; i++) {
      entry.push(i);
    }

    return entry;
  }
</script>
